import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';

import { useSocketContext } from 'app/socket';

import { BaseMessage } from '../config/types/Message';

type UsePreserveScrollTopWhenHistoryMessagesLoadedProps = {
  outerRef: RefObject<HTMLDivElement>;
  innerRef: RefObject<HTMLDivElement>;
  messages: BaseMessage[];
};

// this hook detects when history messages are loaded and calculates the scrollTop
// of outerRef container so the scroll stays on the same position when inner div height is increased
export const usePreserveScrollTopWhenHistoryMessagesLoaded = ({
  outerRef,
  innerRef,
  messages,
}: UsePreserveScrollTopWhenHistoryMessagesLoadedProps) => {
  const prevScrollHeight = useRef<number>(0);
  const prevFirstMessage = useRef<BaseMessage | null>();
  const { isSocketReady } = useSocketContext();

  useEffect(() => {
    if (!isSocketReady) {
      // reset previous values when socket is not ready
      prevFirstMessage.current = undefined;
    }
  }, [isSocketReady]);

  useLayoutEffect(() => {
    // With column-reverse, the first message (oldest) is at index 0
    const isHistoryLoaded =
      prevFirstMessage.current !== undefined &&
      prevFirstMessage.current?.messageId !== messages[0]?.messageId;

    if (!innerRef.current || !outerRef.current) {
      return;
    }

    if (isHistoryLoaded) {
      // When new history messages load, the inner container grows
      // We need to adjust scrollTop to maintain the same visual position
      const newScrollHeight = innerRef.current.scrollHeight;
      const scrollHeightDiff = newScrollHeight - prevScrollHeight.current;

      // Add the height difference to scrollTop to keep the same messages in view
      outerRef.current.scrollTop =
        outerRef.current.scrollTop + scrollHeightDiff;
    }

    prevScrollHeight.current = innerRef?.current?.scrollHeight || 0;

    // Track the first message (oldest message at index 0)
    prevFirstMessage.current = messages[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, outerRef, innerRef]);
};
